/**
 * R2 Workspace Sticky Intelligence Engine (WSIE) — Store
 * Full lifecycle management: Active → Completed → Archived → Deleted
 * Per-user visibility, timeline events, activity log, @mentions.
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

export type NoteType    = 'reminder' | 'action' | 'question' | 'review' | 'approval' | 'due';
export type NoteColor   = 'yellow' | 'blue' | 'green' | 'pink' | 'orange' | 'white' | 'slate';
export type NoteStyle   = 'tape' | 'pin' | 'magnet' | 'bare';
export type NoteStatus  = 'active' | 'completed' | 'archived' | 'deleted';
export type NotePriority= 'low' | 'medium' | 'high' | 'critical';
export type AnchorType  = 'monitor' | 'drawing' | 'bim' | 'room' | 'document' | 'project' | 'map' | '3d';
export type EventKind   = 'created'|'assigned'|'viewed'|'acknowledged'|'edited'|'hidden'|'completed'|'reopened'|'archived'|'deleted'|'snoozed'|'mentioned';

export interface TimelineEvent {
  id:     string;
  kind:   EventKind;
  actor:  string;
  at:     string;   // ISO
  note:   string;
}

export interface NoteAttachment {
  id:       string;
  type:     'image' | 'pdf' | 'voice' | 'video' | 'link' | 'measurement' | 'bim';
  name:     string;
  url:      string;
  addedBy:  string;
  addedAt:  string;
}

export interface StickyNote {
  id:           string;
  type:         NoteType;
  color:        NoteColor;
  style:        NoteStyle;
  title:        string;
  content:      string;
  priority:     NotePriority;
  status:       NoteStatus;
  rotation:     number;   // degrees, subtle (-3 to 3)

  // Position & size (viewport %)
  posX:    number;
  posY:    number;
  width:   number;
  height:  number;
  opacity: number;  // 0.2 – 1.0

  // Ownership
  creator:     string;
  createdAt:   string;
  updatedAt:   string;
  assignedTo:  string;
  mentions:    string[];

  // Anchoring
  anchorType:  AnchorType;
  anchorRoute: string;   // route id where note was created
  anchorLabel: string;   // human-readable context

  // Due date
  dueDate:  string | null;

  // Completion
  completedAt:    string | null;
  completedBy:    string | null;
  completionNote: string;

  // Per-user visibility
  hiddenFor:      string[];
  acknowledgedBy: string[];
  readBy:         string[];
  snoozedUntil:   Record<string, string>; // userId → ISO date

  // Timeline
  timeline:    TimelineEvent[];
  attachments: NoteAttachment[];
}

// ── Color tokens ──────────────────────────────────────────────────────────────
export const NOTE_COLORS: Record<NoteColor, { bg: string; accent: string; text: string; border: string; shadow: string }> = {
  yellow: { bg: '#fef9c3', accent: '#ca8a04', text: '#713f12', border: '#fde047', shadow: 'rgba(202,138,4,0.25)' },
  blue:   { bg: '#dbeafe', accent: '#2563eb', text: '#1e3a5f', border: '#93c5fd', shadow: 'rgba(37,99,235,0.2)' },
  green:  { bg: '#dcfce7', accent: '#16a34a', text: '#14532d', border: '#86efac', shadow: 'rgba(22,163,74,0.2)' },
  pink:   { bg: '#fce7f3', accent: '#db2777', text: '#831843', border: '#f9a8d4', shadow: 'rgba(219,39,119,0.2)' },
  orange: { bg: '#ffedd5', accent: '#ea580c', text: '#7c2d12', border: '#fdba74', shadow: 'rgba(234,88,12,0.22)' },
  white:  { bg: '#f8fafc', accent: '#475569', text: '#1e293b', border: '#cbd5e1', shadow: 'rgba(71,85,105,0.18)' },
  slate:  { bg: '#1e293b', accent: '#5eead4', text: '#e2ecfa', border: '#334155', shadow: 'rgba(94,234,212,0.15)' },
};

export const NOTE_TYPE_META: Record<NoteType, { icon: string; label: string; color: NoteColor }> = {
  reminder: { icon: '📌', label: 'Reminder',         color: 'yellow' },
  action:   { icon: '⚡', label: 'Action Required',  color: 'orange' },
  question: { icon: '❓', label: 'Question',          color: 'blue'   },
  review:   { icon: '🔍', label: 'Review Needed',     color: 'green'  },
  approval: { icon: '🔒', label: 'Approval Required', color: 'pink'   },
  due:      { icon: '📅', label: 'Due Date',           color: 'white'  },
};

// ── Store ─────────────────────────────────────────────────────────────────────
interface WSIEState {
  notes: StickyNote[];
  creating: boolean;
  editingId: string | null;
  showManager: boolean;
  stickyMode: boolean;          // cursor-drop mode — left-click stamps a note
  stickyType: NoteType;         // which note type to stamp
  stickyColor: NoteColor;       // color for stamped notes
  quickEditId: string | null;   // note just stamped, waiting for inline text

  // Actions
  openCreate: () => void;
  closeCreate: () => void;
  setEditingId: (id: string | null) => void;
  setShowManager: (v: boolean) => void;
  setStickyMode: (v: boolean, type?: NoteType, color?: NoteColor) => void;
  setQuickEditId: (id: string | null) => void;

  addNote: (note: StickyNote) => void;
  updateNote: (id: string, patch: Partial<StickyNote>) => void;
  moveNote: (id: string, x: number, y: number) => void;
  completeNote: (id: string, actor: string, comment?: string) => void;
  archiveNote: (id: string, actor: string) => void;
  deleteNote: (id: string, actor: string) => void;
  reopenNote: (id: string, actor: string) => void;
  hideForUser: (id: string, userId: string) => void;
  acknowledgeNote: (id: string, userId: string) => void;
  snoozeNote: (id: string, userId: string, until: string) => void;
  addTimelineEvent: (id: string, event: TimelineEvent) => void;

  // Selectors
  getActiveNotes: (userId?: string) => StickyNote[];
  getNoteById: (id: string) => StickyNote | null;
}

function makeEvent(kind: EventKind, actor: string, note = ''): TimelineEvent {
  return { id: crypto.randomUUID(), kind, actor, at: new Date().toISOString(), note };
}

export const useWSIEStore = create<WSIEState>()(
  persist(
    immer((set, get) => ({
      notes: [],
      creating: false,
      editingId: null,
      showManager: false,
      stickyMode: false,
      stickyType: 'reminder',
      stickyColor: 'yellow',
      quickEditId: null,

      openCreate:       () => set((s) => { s.creating = true; }),
      closeCreate:      () => set((s) => { s.creating = false; }),
      setEditingId:     (id) => set((s) => { s.editingId = id; }),
      setShowManager:   (v)  => set((s) => { s.showManager = v; }),
      setStickyMode:    (v, type = 'reminder', color = 'yellow') =>
        set((s) => { s.stickyMode = v; s.stickyType = type; s.stickyColor = color; if (!v) s.quickEditId = null; }),
      setQuickEditId:   (id) => set((s) => { s.quickEditId = id; }),

      addNote: (note) => set((s) => { s.notes.push(note); }),

      updateNote: (id, patch) => set((s) => {
        const n = s.notes.find((x) => x.id === id);
        if (n) { Object.assign(n, patch); n.updatedAt = new Date().toISOString(); }
      }),

      moveNote: (id, x, y) => set((s) => {
        const n = s.notes.find((x) => x.id === id);
        if (n) { n.posX = x; n.posY = y; }
      }),

      completeNote: (id, actor, comment = '') => set((s) => {
        const n = s.notes.find((x) => x.id === id);
        if (!n) return;
        n.status = 'completed';
        n.completedAt = new Date().toISOString();
        n.completedBy = actor;
        n.completionNote = comment;
        n.updatedAt = new Date().toISOString();
        n.timeline.push(makeEvent('completed', actor, comment));
      }),

      archiveNote: (id, actor) => set((s) => {
        const n = s.notes.find((x) => x.id === id);
        if (!n) return;
        n.status = 'archived';
        n.updatedAt = new Date().toISOString();
        n.timeline.push(makeEvent('archived', actor));
      }),

      deleteNote: (id, actor) => set((s) => {
        const n = s.notes.find((x) => x.id === id);
        if (!n) return;
        n.status = 'deleted';
        n.updatedAt = new Date().toISOString();
        n.timeline.push(makeEvent('deleted', actor));
      }),

      reopenNote: (id, actor) => set((s) => {
        const n = s.notes.find((x) => x.id === id);
        if (!n) return;
        n.status = 'active';
        n.completedAt = null;
        n.completedBy = null;
        n.updatedAt = new Date().toISOString();
        n.timeline.push(makeEvent('reopened', actor));
      }),

      hideForUser: (id, userId) => set((s) => {
        const n = s.notes.find((x) => x.id === id);
        if (n && !n.hiddenFor.includes(userId)) n.hiddenFor.push(userId);
      }),

      acknowledgeNote: (id, userId) => set((s) => {
        const n = s.notes.find((x) => x.id === id);
        if (n && !n.acknowledgedBy.includes(userId)) {
          n.acknowledgedBy.push(userId);
          n.timeline.push(makeEvent('acknowledged', userId));
        }
      }),

      snoozeNote: (id, userId, until) => set((s) => {
        const n = s.notes.find((x) => x.id === id);
        if (n) {
          n.snoozedUntil[userId] = until;
          n.timeline.push(makeEvent('snoozed', userId, `Until ${until}`));
        }
      }),

      addTimelineEvent: (id, event) => set((s) => {
        const n = s.notes.find((x) => x.id === id);
        if (n) n.timeline.push(event);
      }),

      getActiveNotes: (userId = '') => {
        const now = new Date().toISOString();
        return get().notes.filter((n) => {
          if (n.status !== 'active') return false;
          if (userId && n.hiddenFor.includes(userId)) return false;
          if (userId && n.snoozedUntil[userId] && n.snoozedUntil[userId] > now) return false;
          return true;
        });
      },

      getNoteById: (id) => get().notes.find((n) => n.id === id) ?? null,
    })),
    {
      name: 'r2:wsie-store',
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

// ── Helpers ───────────────────────────────────────────────────────────────────
export function makeStickyNote(
  overrides: Partial<StickyNote> & { creator: string; type: NoteType; content: string },
): StickyNote {
  const type  = overrides.type;
  const meta  = NOTE_TYPE_META[type];
  const now   = new Date().toISOString();
  const rotation = (Math.random() - 0.5) * 4; // -2 to 2 degrees

  return {
    id:           crypto.randomUUID(),
    type,
    color:        overrides.color ?? meta.color,
    style:        overrides.style ?? 'tape',
    title:        overrides.title ?? meta.label,
    content:      overrides.content,
    priority:     overrides.priority ?? 'medium',
    status:       'active',
    rotation,
    posX:         overrides.posX ?? 70 + Math.random() * 10,
    posY:         overrides.posY ?? 20 + Math.random() * 10,
    width:        overrides.width  ?? 220,
    height:       overrides.height ?? 160,
    opacity:      overrides.opacity ?? 1.0,
    creator:      overrides.creator,
    createdAt:    now,
    updatedAt:    now,
    assignedTo:   overrides.assignedTo ?? '',
    mentions:     overrides.mentions ?? [],
    anchorType:   overrides.anchorType  ?? 'monitor',
    anchorRoute:  overrides.anchorRoute ?? '',
    anchorLabel:  overrides.anchorLabel ?? 'General Workspace',
    dueDate:      overrides.dueDate ?? null,
    completedAt:  null,
    completedBy:  null,
    completionNote: '',
    hiddenFor:      [],
    acknowledgedBy: [],
    readBy:         [],
    snoozedUntil:   {},
    timeline:    [makeEvent('created', overrides.creator)],
    attachments: [],
  };
}

export function getDueStatus(note: StickyNote): 'ok' | 'soon' | 'overdue' | null {
  if (!note.dueDate) return null;
  const now  = Date.now();
  const due  = new Date(note.dueDate).getTime();
  const diff = due - now;
  if (diff < 0)        return 'overdue';
  if (diff < 86400000 * 2) return 'soon';
  return 'ok';
}
